import { Box, CircularProgress } from "@mui/material";
import { Delete, Download, MoreHoriz } from "@mui/icons-material";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import dayjs from "dayjs";

import RenameChatDialog from "./rename-chat-dialog";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useCopilotStore from "@/store/copilotStore";

interface MessageListProps {
  messages?: any[];
  navigateToChat: (message: any) => void;
  deleteConversation: (message: any) => void;
  openRenameDialog: boolean;
  setOpenRenameDialog: (open: boolean) => void;
  renameConversation: (id: string, newTitle: string) => void;
}

const MessageList = ({
  messages,
  navigateToChat,
  deleteConversation,
  openRenameDialog,
  setOpenRenameDialog,
  renameConversation,
  downloadConversation,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: any) => {
  const copilotStore = useCopilotStore();

  const groupMessagesByTimePeriod = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const oneMonthAgo = new Date(today);
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

    const groups: any = {
      Today: [],
      Yesterday: [],
      "Previous 7 Days": [],
      "Previous 30 Days": [],
    };

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    messages?.forEach((message: any) => {
      const messageDate = new Date(message.timestamp);
      messageDate.setHours(0, 0, 0, 0);

      if (messageDate.getTime() === today.getTime()) {
        groups["Today"].push(message);
      } else if (messageDate.getTime() === yesterday.getTime()) {
        groups["Yesterday"].push(message);
      } else if (messageDate >= oneWeekAgo && messageDate < yesterday) {
        groups["Previous 7 Days"].push(message);
      } else if (messageDate >= oneMonthAgo && messageDate < oneWeekAgo) {
        groups["Previous 30 Days"].push(message);
      } else {
        const year = messageDate.getFullYear();
        const month = monthNames[messageDate.getMonth()];

        const key = `${month} ${year}`;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(message);
      }
    });

    return Object.entries(groups).filter(
      ([_, messages]) => (messages as any).length > 0
    );
  };

  const groupedMessages = groupMessagesByTimePeriod();

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="min-h-[40vh] w-full overflow-auto max-h-[calc(100vh-15rem)]">
      {groupedMessages.map(([groupName, messages], groupIndex) => (
        <div key={groupName} className="relative first:mt-0 last:mb-5">
          <div className="sticky top-0 z-20 text-sm font-extrabold pt-3 pb-2 bg-white chat-group">
            {groupName}
          </div>
          <ul className="space-y-1">
            {(messages as any)?.map((message: any, index: number) => (
              <div
                key={message?.id}
                className={`flex justify-between group hover:bg-brand-primary hover:text-white rounded-sm cursor-pointer ${
                  message.id === copilotStore.currentConversationId
                    ? "bg-brand-primary text-white"
                    : ""
                } ${
                  index === 0 &&
                  groupIndex === 0 &&
                  !dayjs(message?.created_time).isBefore(
                    dayjs().subtract(3, "minute")
                  )
                    ? "relative before:absolute before:inset-0 before:animate-typewriter before:bg-white after:absolute after:inset-0 after:w-[0.125em] after:animate-cursor"
                    : ""
                }`}
              >
                <Box
                  key={message.id}
                  className="w-full"
                  onClick={() => navigateToChat(message)}
                >
                  <button className="w-full text-left py-1 px-1 max-w-[11.6rem]">
                    <span
                      className="block text-sm overflow-hidden whitespace-nowrap font-medium"
                      title={`${message?.title} | ${dayjs(message.created_time).format("MMM D, YYYY h:mm A")}`}
                    >
                      {message.title}
                    </span>
                  </button>
                </Box>
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <MoreHoriz />
                    </span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() => downloadConversation(message)}
                    >
                      <Download className="mx-1" />
                      Download
                    </DropdownMenuItem>
                    <RenameChatDialog
                      conversationId={message?.id}
                      currentTitle={message?.title}
                      onRename={() => {}}
                      open={openRenameDialog}
                      setOpen={setOpenRenameDialog}
                      renameConversation={renameConversation}
                    />
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => deleteConversation(message)}
                    >
                      <Delete className="mx-1" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </ul>
        </div>
      ))}
      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <CircularProgress size={24} color="primary" />
        </div>
      )}

      <div ref={ref} className="h-10" />
    </div>
  );
};

export default MessageList;
